// Global keyboard shortcut registry controller.
// Mount on <body> to capture all keyboard events.
//
// Other Stimulus controllers register/deregister shortcuts by dispatching
// custom events on the body element:
//
//   this.dispatch("shortcut:register", {
//     detail: { key: "g i", description: "Go to Inbox", category: "Navigation", handler: () => { ... } }
//   })
//
//   this.dispatch("shortcut:deregister", { detail: { key: "g i" } })
//
// The registry also exposes a "shortcut:list" event that returns all registered shortcuts
// for use by the command palette.

import { Controller } from "@hotwired/stimulus"

const IGNORED_ELEMENTS = new Set(["INPUT", "TEXTAREA", "SELECT"])

export default class extends Controller {
  connect() {
    this.shortcuts = new Map()
    this.pendingPrefix = null
    this.pendingTimeout = null

    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleRegister = this.handleRegister.bind(this)
    this.handleDeregister = this.handleDeregister.bind(this)
    this.handleList = this.handleList.bind(this)

    this.handleExecute = this.handleExecute.bind(this)

    document.addEventListener("keydown", this.handleKeydown)
    this.element.addEventListener("shortcut:register", this.handleRegister)
    this.element.addEventListener("shortcut:deregister", this.handleDeregister)
    this.element.addEventListener("shortcut:list", this.handleList)
    this.element.addEventListener("shortcut:execute", this.handleExecute)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.element.removeEventListener("shortcut:register", this.handleRegister)
    this.element.removeEventListener("shortcut:deregister", this.handleDeregister)
    this.element.removeEventListener("shortcut:list", this.handleList)
    this.element.removeEventListener("shortcut:execute", this.handleExecute)
    this.clearPendingPrefix()
    this.shortcuts.clear()
  }

  handleRegister(event) {
    const { key, description, category, handler } = event.detail
    if (!key || !handler) return

    this.shortcuts.set(key, { description, category: category || "General", handler })
  }

  handleDeregister(event) {
    const { key } = event.detail
    this.shortcuts.delete(key)
  }

  handleList(event) {
    const list = []
    this.shortcuts.forEach((value, key) => {
      list.push({ key, ...value })
    })
    event.detail.callback(list)
  }

  handleExecute(event) {
    const { key } = event.detail
    const shortcut = this.shortcuts.get(key)
    if (shortcut) shortcut.handler()
  }

  handleKeydown(event) {
    if (this.shouldIgnoreEvent(event)) return

    // Never intercept Cmd/Ctrl combos – let the browser handle them
    if (event.metaKey || event.ctrlKey) return

    const keyStr = this.normalizeKey(event)

    // Handle two-key sequences (e.g. "g i")
    if (this.pendingPrefix) {
      const combo = `${this.pendingPrefix} ${keyStr}`
      this.clearPendingPrefix()

      if (this.shortcuts.has(combo)) {
        event.preventDefault()
        this.shortcuts.get(combo).handler()
        return
      }
      // Fall through if combo not found
    }

    // Check if this key is a prefix for any two-key combo
    if (this.isPrefixKey(keyStr)) {
      this.pendingPrefix = keyStr
      this.pendingTimeout = setTimeout(() => {
        this.pendingPrefix = null
      }, 800)
      return
    }

    // Single key shortcut
    if (this.shortcuts.has(keyStr)) {
      event.preventDefault()
      this.shortcuts.get(keyStr).handler()
    }
  }

  shouldIgnoreEvent(event) {
    const target = event.target
    if (IGNORED_ELEMENTS.has(target.tagName)) return true
    if (target.isContentEditable) return true
    // Ignore when any overlay/dialog is open (except command palette which handles itself)
    if (document.querySelector("dialog[open]")) return true
    return false
  }

  normalizeKey(event) {
    const parts = []
    if (event.ctrlKey) parts.push("ctrl")
    if (event.altKey) parts.push("alt")
    if (event.shiftKey && event.key.length > 1) parts.push("shift")

    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key
    parts.push(key)
    return parts.join("+")
  }

  isPrefixKey(key) {
    for (const shortcutKey of this.shortcuts.keys()) {
      if (shortcutKey.startsWith(`${key} `)) return true
    }
    return false
  }

  clearPendingPrefix() {
    this.pendingPrefix = null
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout)
      this.pendingTimeout = null
    }
  }
}
